import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useState } from 'react';
import {
  useGetSupplierQuery,
  useGetSupplierHistoryQuery,
  useCreateSupplierPaymentMutation,
  useDeleteSupplierPaymentMutation,
  useDeletePurchaseReturnMutation,
  useAllocateSupplierAdvanceMutation,
} from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

function SupplierDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [allocateAmount, setAllocateAmount] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: getLocalDateString(),
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  const { data: supplier, isLoading: loadingSupplier, error: supplierError } = useGetSupplierQuery(id);
  const { data: history, isLoading: loadingHistory, error: historyError } = useGetSupplierHistoryQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [createPayment] = useCreateSupplierPaymentMutation();
  const [deletePayment, { isLoading: deletingPayment }] = useDeleteSupplierPaymentMutation();
  const [deleteReturn, { isLoading: deletingReturn }] = useDeletePurchaseReturnMutation();
  const [allocateAdvance, { isLoading: allocatingAdvance }] = useAllocateSupplierAdvanceMutation();

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment record? This will reverse the ledger journal entries and advance/outstanding balances.')) {
      try {
        await deletePayment({ supplierId: id, paymentId }).unwrap();
      } catch (err) {
        alert(err?.data?.message || err?.message || 'Error deleting payment');
      }
    }
  };

  const handleDeleteReturn = async (returnId) => {
    if (window.confirm('Are you sure you want to delete this purchase return? This will return the stock back to inventory and reverse the journal entries.')) {
      try {
        await deleteReturn(returnId).unwrap();
      } catch (err) {
        alert(err?.data?.message || err?.message || 'Error deleting return');
      }
    }
  };

  if (loadingSupplier || loadingHistory) {
    return <Typography>Loading...</Typography>;
  }
  if (supplierError) {
    return <Typography color="error">Error loading supplier</Typography>;
  }
  if (historyError) {
    return <Typography color="error">Error loading purchase history</Typography>;
  }

  const purchaseOrders = history?.purchaseOrders || [];
  const purchaseReturns = history?.purchaseReturns || [];
  const supplierPayments = history?.supplierPayments || [];
  const openingBalance = Number(supplier?.openingBalance || 0);
  const totalPurchasesAmount = purchaseOrders.reduce(
    (sum, po) => sum + Number(po.totalAmount || 0),
    0
  );
  const totalReturnsAmount = purchaseReturns.reduce(
    (sum, pr) => sum + Number(pr.totalAmount || 0),
    0
  );
  const totalPaymentsAmount = supplierPayments.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  const totalCredits = openingBalance + totalPurchasesAmount;
  const totalDebits = totalPaymentsAmount + totalReturnsAmount;
  const openBalance = history?.summary?.netPayable != null
    ? Number(history.summary.netPayable)
    : Math.max(0, totalCredits - totalDebits);
  const advanceBalance = history?.summary?.availableAdvance != null
    ? Number(history.summary.availableAdvance)
    : Math.max(0, totalDebits - totalCredits);

  const handleSubmitPayment = async () => {
    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) {
      alert('Amount must be greater than zero');
      return;
    }
    try {
      await createPayment({
        supplierId: id,
        ...paymentForm,
      }).unwrap();
      setPaymentDialogOpen(false);
      setPaymentForm((prev) => ({
        ...prev,
        amount: '',
        referenceNo: '',
        notes: '',
      }));
    } catch (error) {
      alert(error?.data?.message || 'Error recording payment');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/purchases/suppliers')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {supplier?.name || 'Supplier'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {supplier?.code && `Code: ${supplier.code}  · `}{supplier?.email || supplier?.phone || ''}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<SummarizeOutlinedIcon />}
            onClick={() => navigate(`/reports/supplier-ledger?supplierId=${id}`)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Statement of Account
          </Button>
          {advanceBalance > 0 && openBalance > 0 && (
            <Button
              variant="contained"
              color="success"
              sx={{ borderRadius: 2 }}
              onClick={() => {
                setAllocateAmount(Math.min(advanceBalance, openBalance).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)));
                setAllocateDialogOpen(true);
              }}
            >
              Apply Advance to Outstanding Balance
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddShoppingCartIcon />}
            onClick={() => navigate(`/purchases/orders/new?supplierId=${id}`)}
            sx={{ borderRadius: 2 }}
          >
            Create Purchase Order
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate(`/purchases/orders/new?supplierId=${id}&lpo=1`)}
            sx={{ borderRadius: 2 }}
          >
            Create LPO
          </Button>
          <Button variant="outlined" onClick={() => setPaymentDialogOpen(true)} sx={{ borderRadius: 2 }}>
            {openBalance > 0 ? 'Record Payment' : 'Pay Advance'}
          </Button>
        </Box>
      </Box>

      {/* Summary KPI Badges */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
            Outstanding Payables
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: openBalance > 0 ? '#ea580c' : '#15803d', mt: 0.5 }}>
            {formatAmount(openBalance)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Net Ledger Balance
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>
            Total Payments Made
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#15803d', mt: 0.5 }}>
            {formatAmount(totalPaymentsAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {supplierPayments.length} payment{supplierPayments.length === 1 ? '' : 's'} recorded
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>
            Total Purchase Returns
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#b91c1c', mt: 0.5 }}>
            {formatAmount(totalReturnsAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {purchaseReturns.length} return{purchaseReturns.length === 1 ? '' : 's'} recorded
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
            Total Invoiced Purchases
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>
            {formatAmount(totalPurchasesAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {purchaseOrders.length} order{purchaseOrders.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        {openingBalance > 0 && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Opening Payable Balance
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#475569', mt: 0.5 }}>
              {formatAmount(openingBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Initial starting balance
            </Typography>
          </Box>
        )}

        {advanceBalance > 0 && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', minWidth: 190, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, textTransform: 'uppercase' }}>
              Available Advance / Credit
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d4ed8', mt: 0.5 }}>
              {formatAmount(advanceBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Unapplied debit balance
            </Typography>
          </Box>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Purchase Orders (${purchaseOrders.length})`} />
        <Tab label={`Purchase Returns (${purchaseReturns.length})`} />
        <Tab label={`Payments History (${supplierPayments.length})`} />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Purchase Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Balance (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.length > 0 ? (
                    purchaseOrders.map((po) => (
                      <TableRow key={po.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{po.orderNumber}</TableCell>
                        <TableCell>{po.invoiceNumber || '-'}</TableCell>
                        <TableCell>
                          {po.invoiceDate ? new Date(po.invoiceDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(po.totalAmount)}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {formatAmount(po.balanceAmount)}
                        </TableCell>
                        <TableCell>{po.status}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/purchases/orders/${po.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No purchase orders for this supplier.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Purchase Returns (Debit Notes)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Return Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Return Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Settlement Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseReturns.length > 0 ? (
                    purchaseReturns.map((pr) => {
                      const method = pr.refundMethod || 'credit';
                      return (
                        <TableRow key={pr.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{pr.returnNumber}</TableCell>
                          <TableCell>
                            {pr.returnDate ? new Date(pr.returnDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {method === 'credit' && (
                              <Typography variant="caption" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                                On-Account (Debit Note)
                              </Typography>
                            )}
                            {method === 'cash' && (
                              <Typography variant="caption" sx={{ bgcolor: '#dcfce7', color: '#15803d', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                                Cash Refund
                              </Typography>
                            )}
                            {method === 'bank' && (
                              <Typography variant="caption" sx={{ bgcolor: '#ede9fe', color: '#6d28d9', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                                Bank Deposit
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {formatAmount(pr.totalAmount)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/purchases/returns/${pr.id}`)}
                              title="View Details"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete Return & Reverse Stock/Ledgers"
                              disabled={deletingReturn}
                              onClick={() => handleDeleteReturn(pr.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No purchase returns for this supplier.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Supplier Payments & Advances
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Payment Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Allocations / Note</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supplierPayments.length > 0 ? (
                    supplierPayments.map((p) => {
                      const allocSum = (p.allocations || []).reduce((acc, a) => acc + Number(a.amount || 0), 0);
                      const isFullAdvance = allocSum === 0;
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{p.referenceNo || '-'}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{p.paymentMethod || 'cash'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatAmount(p.amount)}
                          </TableCell>
                          <TableCell>
                            {isFullAdvance ? (
                              <Typography variant="caption" sx={{ bgcolor: 'success.light', color: 'success.dark', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600 }}>
                                Advance Credit
                              </Typography>
                            ) : (
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  Applied to bills: {formatAmount(allocSum)}
                                </Typography>
                                {Number(p.amount) > allocSum && (
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    + {formatAmount(Number(p.amount) - allocSum)} Advance
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {p.notes && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.3 }}>
                                Note: {p.notes}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              color="error"
                              title="Delete Payment & Reverse Ledgers"
                              disabled={deletingPayment}
                              onClick={() => handleDeletePayment(p.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No payment records found for this supplier.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Supplier Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Payment Date"
              value={paymentForm.paymentDate}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="number"
              label={`Amount (${currency})`}
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={openBalance > 0 ? `Outstanding: ${formatAmount(openBalance)}` : ''}
            />
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Reference No"
              value={paymentForm.referenceNo}
              onChange={(e) => setPaymentForm({ ...paymentForm, referenceNo: e.target.value })}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPayment}>
            Save Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={allocateDialogOpen}
        onClose={() => setAllocateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Apply Advance to Outstanding Balance</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Available Advance: <strong>{formatAmount(advanceBalance)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Outstanding Credit: <strong>{formatAmount(openBalance)}</strong>
          </Typography>
          <TextField
            type="number"
            label={`Amount to Apply (${currency}) *`}
            value={allocateAmount}
            onChange={(e) => setAllocateAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 0.01, max: Math.min(advanceBalance, openBalance), step: 0.01 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setAllocateDialogOpen(false)} disabled={allocatingAdvance}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={allocatingAdvance}
              onClick={async () => {
                const amt = Number(allocateAmount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                if (amt > advanceBalance) {
                  alert(`Amount cannot exceed available advance balance (${formatAmount(advanceBalance)})`);
                  return;
                }
                try {
                  await allocateAdvance({
                    supplierId: id,
                    amount: amt,
                  }).unwrap();
                  setAllocateDialogOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error allocating advance');
                }
              }}
            >
              {allocatingAdvance ? 'Applying...' : 'Apply Advance'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SupplierDetails;

